import { supabase } from "@/integrations/supabase/client";

// Email validation function to prevent injection attacks
const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== "string") return false;

  // Basic email regex that's secure and follows RFC 5322 guidelines
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  // Block dangerous characters and patterns
  const dangerousPatterns =
    /<|>|javascript:|script|onload|onerror|expression\(|@import/i;

  return (
    emailRegex.test(email.trim()) &&
    !dangerousPatterns.test(email) &&
    email.length <= 254
  ); // RFC 5321 limit
};

// Sanitize string input to prevent XSS
const sanitizeString = (input: string): string => {
  if (!input || typeof input !== "string") return "";

  return input
    .trim()
    .replace(/[<>"'&]/g, (char) => {
      switch (char) {
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        case "'":
          return "&#x27;";
        case "&":
          return "&amp;";
        default:
          return char;
      }
    })
    .substring(0, 1000); // Reasonable length limit
};

export interface GuestInvitation {
  id: string;
  host_user_id: string;
  guest_email: string;
  guest_user_id?: string;
  project_id?: string;
  invitation_token: string;
  status:
    | "pending"
    | "accepted"
    | "declined"
    | "expired"
    | "cancelled"
    | "deleted";
  permissions: {
    view: boolean;
    edit: boolean;
  };
  invited_at: string;
  accepted_at?: string;
  expires_at: string;
}

export interface GuestPermission {
  id: string;
  host_user_id: string;
  guest_user_id: string;
  project_id?: string;
  permissions: {
    view: boolean;
    edit: boolean;
  };
  is_active: boolean;
  created_at: string;
  guest_user?: {
    id: string;
    email: string;
    created_at: string;
  };
}

export interface SubscriptionLimits {
  tier: "PAYG" | "professional";
  max_guest_invitations: number | null; // null = no static cap (PAYG billed per-invite)
  max_concurrent_projects: number;
  max_reports_per_month: number;
}

export class GuestInvitationService {
  /**
   * Get subscription limits for a specific tier
   */
  static async getSubscriptionLimits(
    tier: string,
  ): Promise<SubscriptionLimits | null> {
    try {
      const { data, error } = await supabase
        .from("subscription_limits")
        .select("*")
        .eq("tier", tier.toLowerCase())
        .maybeSingle();

      if (error) {
        if (error.code === "42P01") {
          // Table doesn't exist, return default limits based on tier
          console.warn(
            "subscription_limits table does not exist yet, using defaults",
          );
          const defaultLimits = {
            PAYG: {
              tier: "PAYG" as const,
              max_guest_invitations: 10,
              max_concurrent_projects: 10,
              max_reports_per_month: 20,
            },
            professional: {
              tier: "professional" as const,
              max_guest_invitations: 10,
              max_concurrent_projects: 10,
              max_reports_per_month: 20,
            },
          };
          return (defaultLimits[
            tier.toUpperCase() as keyof typeof defaultLimits
          ] || defaultLimits.PAYG) as any;
        }
        throw error;
      }
      // If row doesn't exist, fall back to defaults (avoids PGRST116 / 406 noise)
      if (!data) {
        return {
          tier: "PAYG",
          max_guest_invitations: 10,
          max_concurrent_projects: 10,
          max_reports_per_month: 20,
        };
      }

      return data as any;
    } catch (error) {
      console.error("Error fetching subscription limits:", error);
      // Return default limits for PAYG tier
      return {
        tier: "PAYG",
        max_guest_invitations: 10,
        max_concurrent_projects: 10,
        max_reports_per_month: 20,
      };
    }
  }

  /**
   * Check if user can invite more guests based on their subscription tier
   */
  static async canInviteGuest(userId: string): Promise<{
    allowed: boolean;
    currentCount: number;
    maxAllowed: number;
    message: string;
  }> {
    try {
      const { data, error } = await supabase.rpc("can_invite_guest", {
        host_id: userId,
      });

      if (error) throw error;

      // Get current counts and limits
      const [guestCount, userProfile] = await Promise.all([
        this.getActiveGuestCount(userId),
        this.getUserSubscriptionTier(userId),
      ]);

      const limits = await this.getSubscriptionLimits(userProfile.tier);

      if (!limits) {
        return {
          allowed: false,
          currentCount: guestCount,
          maxAllowed: 0,
          message: "Unable to determine subscription limits",
        };
      }

      // Determine allowance: if max_guest_invitations is NULL => PAYG (no static cap)
      const maxAllowed = limits.max_guest_invitations;
      const allowed = maxAllowed === null ? true : guestCount < maxAllowed;
      const message =
        maxAllowed === null
          ? `Invite additional guests (will cost 1 credit per invite).`
          : allowed
            ? `You can invite ${maxAllowed - guestCount} more guest${maxAllowed - guestCount !== 1 ? "s" : ""}`
            : `Guest limit reached (${guestCount}/${maxAllowed}). Upgrade to invite more collaborators.`;

      return {
        allowed,
        currentCount: guestCount,
        maxAllowed: maxAllowed ?? 0,
        message,
      };
    } catch (error) {
      console.error("Error checking guest invitation eligibility:", error);
      return {
        allowed: false,
        currentCount: 0,
        maxAllowed: 0,
        message: "Error checking invitation eligibility",
      };
    }
  }

  /**
   * Get active guest count for a user
   */
  static async getActiveGuestCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from("guest_permissions")
        .select("id", { count: "exact", head: true })
        .eq("host_user_id", userId)
        .eq("is_active", true);

      if (error) throw error;
      return count ?? 0;
    } catch (error) {
      console.error("Error getting active guest count:", error);
      return 0;
    }
  }

  /**
   * Get user subscription tier (simplified - would integrate with actual subscription system)
   */
  static async getUserSubscriptionTier(
    userId: string,
  ): Promise<{ tier: "free" | "standard" | "professional" }> {
    try {
      // Use RPC function to get user tier instead of direct auth.users access
      const { data, error } = await supabase.rpc("get_user_subscription_tier", {
        user_id: userId,
      });

      if (error) {
        console.error("Error getting user subscription tier:", error);
        return { tier: "free" };
      }

      return { tier: (data || "free") as "free" | "standard" | "professional" };
    } catch (error) {
      console.error("Error getting user subscription tier:", error);
      return { tier: "free" };
    }
  }

  /**
   * Send a guest invitation
   */
  static async sendInvitation(
    hostUserId: string,
    guestEmail: string,
    projectId?: string,
    permissions: { view: boolean; edit: boolean } = { view: true, edit: false },
  ): Promise<{
    success: boolean;
    invitation?: GuestInvitation;
    token?: string;
    emailSent?: boolean;
    invitationUrl?: string;
    emailError?: string;
    error?: string;
  }> {
    try {
      // Input validation
      if (!hostUserId || typeof hostUserId !== "string") {
        return { success: false, error: "Invalid host user ID" };
      }

      if (!isValidEmail(guestEmail)) {
        return { success: false, error: "Invalid email address" };
      }

      // Sanitize inputs
      const sanitizedEmail = sanitizeString(guestEmail.toLowerCase());
      const sanitizedProjectId = projectId
        ? sanitizeString(projectId)
        : undefined;

      // Check if user can invite more guests
      const eligibility = await this.canInviteGuest(hostUserId);

      if (!eligibility.allowed) {
        return {
          success: false,
          error: eligibility.message,
        };
      }

      // Check if invitation already exists for this email/project combination
      // Use a safer query that won't trigger RLS issues
      let existingQuery = supabase
        .from("guest_invitations")
        .select(
          "id, host_user_id, guest_email, project_id, invitation_token, permissions, status, invited_at, expires_at",
        )
        .eq("host_user_id", hostUserId)
        .eq("guest_email", sanitizedEmail)
        .eq("status", "pending");

      // Handle null project_id properly
      if (sanitizedProjectId === null || sanitizedProjectId === undefined) {
        existingQuery = existingQuery.is("project_id", null);
      } else {
        existingQuery = existingQuery.eq("project_id", sanitizedProjectId);
      }

      const { data: existingInvitations, error: checkError } =
        await existingQuery;

      // Handle the query gracefully even if it fails due to RLS
      if (
        !checkError &&
        existingInvitations &&
        existingInvitations.length > 0
      ) {
        // If there's already a pending invitation, treat this as a "resend" operation.
        const existing = existingInvitations[0] as any as GuestInvitation & {
          invitation_token?: string;
        };

        const existingToken = (existing as any).invitation_token as
          | string
          | undefined;

        if (existingToken) {
          const emailResult = await this.sendInvitationEmail(
            existing,
            existingToken,
          );
          return {
            success: true,
            invitation: existing,
            token: existingToken,
            emailSent: emailResult.emailSent,
            invitationUrl: emailResult.invitationUrl,
            emailError: emailResult.error,
          };
        }

        // Extremely defensive fallback: if token is missing, behave like "already pending".
        return {
          success: true,
          invitation: existing,
          token: undefined,
          emailSent: false,
          invitationUrl: undefined,
          emailError:
            "An invitation is already pending for this guest, but its token could not be loaded. Please contact support.",
        };
      }

      // Generate invitation token
      const invitationToken = crypto.randomUUID();

      // Create new invitation with sanitized data
      const { data, error } = await supabase
        .from("guest_invitations")
        .insert({
          host_user_id: hostUserId,
          guest_email: sanitizedEmail,
          project_id: sanitizedProjectId || null,
          invitation_token: invitationToken,
          permissions,
          status: "pending",
          expires_at: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000,
          ).toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Send email notification to guest
      const emailResult = await this.sendInvitationEmail(data, invitationToken);

      return {
        success: true,
        invitation: data,
        token: invitationToken,
        emailSent: emailResult.emailSent,
        invitationUrl: emailResult.invitationUrl,
        emailError: emailResult.error,
      };
    } catch (error) {
      console.error("Error sending invitation:", error);
      return {
        success: false,
        error: "Failed to send invitation",
      };
    }
  }

  /**
   * Get all invitations sent by a user
   */
  static async getHostInvitations(
    hostUserId: string,
  ): Promise<GuestInvitation[]> {
    try {
      const { data, error } = await supabase
        .from("guest_invitations")
        .select("*")
        .eq("host_user_id", hostUserId)
        .order("invited_at", { ascending: false });

      if (error) {
        if (error.code === "42P01") {
          // Table doesn't exist, return empty array
          console.warn("guest_invitations table does not exist yet");
          return [];
        }
        throw error;
      }
      return data || [];
    } catch (error) {
      console.error("Error fetching host invitations:", error);
      return [];
    }
  }

  /**
   * Get all invitations received by a user
   */
  static async getGuestInvitations(
    userEmail: string,
  ): Promise<GuestInvitation[]> {
    try {
      const { data, error } = await supabase
        .from("guest_invitations")
        .select("*")
        .eq("guest_email", userEmail)
        .in("status", ["pending", "accepted"])
        .order("invited_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching guest invitations:", error);
      return [];
    }
  }

  /**
   * Accept a guest invitation
   */
  static async acceptInvitation(
    invitationId: string,
    userId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // First, update the invitation status and get the invitation data
      const { data: invitation, error: updateError } = await supabase
        .from("guest_invitations")
        .update({
          status: "accepted",
          guest_user_id: userId,
          accepted_at: new Date().toISOString(),
        })
        .eq("id", invitationId)
        .eq("status", "pending")
        .select()
        .single();

      if (updateError) throw updateError;

      if (!invitation) {
        return {
          success: false,
          error: "Invitation not found or already processed",
        };
      }

      // Create the corresponding guest permission record
      console.log("📝 Creating guest permission for:", {
        host_user_id: invitation.host_user_id,
        guest_user_id: userId,
        project_id: invitation.project_id,
        permissions: invitation.permissions,
      });

      const { error: permissionError } = await supabase
        .from("guest_permissions")
        .insert({
          host_user_id: invitation.host_user_id,
          guest_user_id: userId,
          project_id: invitation.project_id,
          permissions: invitation.permissions,
          is_active: true,
        });

      if (permissionError) {
        // If guest permissions table doesn't exist, warn but don't fail the invitation acceptance
        if (permissionError.code === "42P01") {
          console.warn("guest_permissions table does not exist yet");
          return { success: true }; // Still mark invitation as accepted
        }

        console.error("Error creating guest permissions:", permissionError);
        // Rollback the invitation update if permission creation fails
        await supabase
          .from("guest_invitations")
          .update({ status: "pending", guest_user_id: null, accepted_at: null })
          .eq("id", invitationId);

        throw permissionError;
      }

      console.log(
        `Successfully accepted invitation and created guest permissions for user ${userId} on project ${invitation.project_id}`,
      );
      return { success: true };
    } catch (error) {
      console.error("Error accepting invitation:", error);
      return {
        success: false,
        error: "Failed to accept invitation",
      };
    }
  }

  /**
   * Decline a guest invitation
   */
  static async declineInvitation(
    invitationId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("guest_invitations")
        .update({ status: "declined" })
        .eq("id", invitationId)
        .eq("status", "pending")
        .select()
        .single();

      if (error) throw error;

      if (!data) {
        return {
          success: false,
          error: "Invitation not found or already processed",
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Error declining invitation:", error);
      return {
        success: false,
        error: "Failed to decline invitation",
      };
    }
  }

  /**
   * Update guest permissions
   */
  static async updateGuestPermissions(
    hostUserId: string,
    guestUserId: string,
    projectId: string | null,
    permissions: { view: boolean; edit: boolean },
  ): Promise<{ success: boolean; error?: string }> {
    try {
      let query = supabase
        .from("guest_permissions")
        .update({ permissions })
        .eq("host_user_id", hostUserId)
        .eq("guest_user_id", guestUserId)
        .eq("is_active", true);

      // Handle project_id properly
      if (projectId === null || projectId === undefined) {
        // For null project_id, match both NULL and empty string records
        query = query.or("project_id.is.null,project_id.eq.");
      } else {
        query = query.eq("project_id", projectId);
      }

      const { data, error } = await query.select();

      if (error) {
        if (error.code === "42P01") {
          console.warn("guest_permissions table does not exist yet");
          return {
            success: false,
            error: "Database tables are not set up yet",
          };
        }
        console.error("Update error details:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        return {
          success: false,
          error: "No guest permission record found to update",
        };
      }

      console.log(
        `Successfully updated ${data.length} guest permission record(s)`,
      );
      return { success: true };
    } catch (error) {
      console.error("Error updating guest permissions:", error);
      return {
        success: false,
        error: "Failed to update permissions",
      };
    }
  }

  /**
   * Remove a guest (deactivate permissions)
   */
  static async removeGuest(
    hostUserId: string,
    guestUserId: string,
    projectId?: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      let permissionsQuery = supabase
        .from("guest_permissions")
        .update({ is_active: false })
        .eq("host_user_id", hostUserId)
        .eq("guest_user_id", guestUserId);

      // If projectId is specified, filter by it. Otherwise, remove all permissions for this guest
      if (projectId !== undefined) {
        if (projectId === null || projectId === "") {
          permissionsQuery = permissionsQuery.or(
            "project_id.is.null,project_id.eq.",
          );
        } else {
          permissionsQuery = permissionsQuery.eq("project_id", projectId);
        }
      }

      // Also update any related invitations to "cancelled" status for better UX
      let invitationsQuery = supabase
        .from("guest_invitations")
        .update({ status: "cancelled" })
        .eq("host_user_id", hostUserId)
        .eq("guest_user_id", guestUserId)
        .in("status", ["pending", "accepted"]);

      if (projectId !== undefined) {
        if (projectId === null || projectId === "") {
          invitationsQuery = invitationsQuery.or(
            "project_id.is.null,project_id.eq.",
          );
        } else {
          invitationsQuery = invitationsQuery.eq("project_id", projectId);
        }
      }

      // Execute both updates
      const [permissionsResult, invitationsResult] = await Promise.all([
        permissionsQuery,
        invitationsQuery,
      ]);

      if (permissionsResult.error) {
        if (permissionsResult.error.code === "42P01") {
          console.warn("guest_permissions table does not exist yet");
          return {
            success: false,
            error: "Database tables are not set up yet",
          };
        }
        console.error(
          "Error removing guest permissions:",
          permissionsResult.error,
        );
        throw permissionsResult.error;
      }

      if (invitationsResult.error) {
        console.warn(
          "Error updating invitation status:",
          invitationsResult.error,
        );
        // Don't fail the whole operation if invitation update fails
      }

      console.log(
        `Successfully deactivated guest permissions and cancelled invitations for user ${guestUserId} under host ${hostUserId}`,
      );
      return { success: true };
    } catch (error) {
      console.error("Error removing guest:", error);
      return {
        success: false,
        error: "Failed to remove guest",
      };
    }
  }

  /**
   * Get active guest permissions for a host, optionally filtered to a single project.
   */
  static async getHostGuestPermissions(
    hostUserId: string,
    projectId?: string | null,
  ): Promise<GuestPermission[]> {
    try {
      let query = supabase
        .from("guest_permissions")
        .select("*")
        .eq("host_user_id", hostUserId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (projectId !== undefined) {
        query =
          projectId === null
            ? query.is("project_id", null)
            : query.eq("project_id", projectId);
      }

      const { data: permissions, error } = await query;

      if (error) {
        if (error.code === "42P01") {
          // Table doesn't exist, return empty array
          console.warn("guest_permissions table does not exist yet");
          return [];
        }
        console.error("Error fetching guest permissions:", error);
        throw error;
      }

      if (!permissions || permissions.length === 0) {
        return [];
      }

      // Try to get guest emails from accepted invitations
      const guestUserIds = permissions.map((p) => p.guest_user_id);
      const { data: invitations, error: invitationsError } = await supabase
        .from("guest_invitations")
        .select("guest_user_id, guest_email")
        .in("guest_user_id", guestUserIds)
        .eq("status", "accepted");

      let emailMap = new Map();
      if (!invitationsError && invitations) {
        invitations.forEach((inv) => {
          if (inv.guest_user_id) {
            emailMap.set(inv.guest_user_id, inv.guest_email);
          }
        });
      }

      // Merge permissions with available email data
      const permissionsWithUsers = permissions.map((permission) => ({
        ...permission,
        guest_user: emailMap.has(permission.guest_user_id)
          ? {
              id: permission.guest_user_id,
              email: emailMap.get(permission.guest_user_id),
              created_at: permission.created_at,
            }
          : null,
      }));

      console.log(
        `Found ${permissionsWithUsers.length} active guest permissions for host ${hostUserId}`,
      );
      console.log(
        "Permissions:",
        permissionsWithUsers.map((p) => ({
          id: p.id,
          guest_user_id: p.guest_user_id,
          is_active: p.is_active,
          guest_email: p.guest_user?.email,
        })),
      );

      return permissionsWithUsers;
    } catch (error) {
      console.error("Error fetching guest permissions:", error);
      return [];
    }
  }

  /**
   * Check if a user has access to a project as a guest
   */
  static async checkGuestAccess(
    userId: string,
    hostUserId: string,
    projectId?: string,
  ): Promise<{
    hasAccess: boolean;
    permissions?: { view: boolean; edit: boolean };
  }> {
    try {
      let query = supabase
        .from("guest_permissions")
        .select("permissions")
        .eq("host_user_id", hostUserId)
        .eq("guest_user_id", userId)
        .eq("is_active", true);

      // Handle null project_id properly
      if (projectId === null || projectId === undefined) {
        query = query.is("project_id", null);
      } else {
        query = query.eq("project_id", projectId);
      }

      const { data, error } = await query.single();

      if (error || !data) {
        return { hasAccess: false };
      }

      return {
        hasAccess: true,
        permissions: data.permissions,
      };
    } catch (error) {
      console.error("Error checking guest access:", error);
      return { hasAccess: false };
    }
  }

  /**
   * Get projects that a user has guest access to
   */
  static async getGuestProjects(userId: string): Promise<GuestPermission[]> {
    try {
      console.log("🔍 getGuestProjects called for userId:", userId);
      const { data: permissions, error } = await supabase
        .from("guest_permissions")
        .select(
          `
          *
        `,
        )
        .eq("guest_user_id", userId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      console.log("📊 Guest permissions query result:", {
        count: permissions?.length || 0,
        error,
      });

      if (error) {
        if (error.code === "42P01") {
          console.warn("guest_permissions table does not exist yet");
          return [];
        }
        if (error.code === "42501") {
          console.error(
            "❌ Permission denied accessing guest_permissions. Check RLS policies:",
            error,
          );
          return [];
        }
        console.error("Error fetching guest projects:", error);
        return []; // Return empty array instead of throwing to avoid breaking the app
      }

      // If we have permissions, fetch host user details from profiles table
      if (permissions && permissions.length > 0) {
        const hostUserIds = [
          ...new Set(permissions.map((p) => p.host_user_id)),
        ];

        // Fetch host user details from profiles table
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", hostUserIds);

        if (profilesError) {
          console.warn("Could not fetch host user profiles:", profilesError);
        }

        // Also get email addresses from auth metadata (this is a fallback approach)
        const enrichedPermissions = await Promise.all(
          permissions.map(async (permission) => {
            const profile = profiles?.find(
              (p) => p.id === permission.host_user_id,
            );

            return {
              ...permission,
              host_user: {
                id: permission.host_user_id,
                email: profile?.full_name || "Unknown Host", // Using full_name as display name
                created_at: permission.created_at, // Use permission creation date as fallback
              },
            };
          }),
        );

        return enrichedPermissions;
      }

      return permissions || [];
    } catch (error) {
      console.error("Error fetching guest projects:", error);
      return [];
    }
  }

  /**
   * Cleanup expired invitations
   */
  static async cleanupExpiredInvitations(): Promise<number> {
    try {
      const { data, error } = await supabase.rpc("cleanup_expired_invitations");

      if (error) throw error;
      return data || 0;
    } catch (error) {
      console.error("Error cleaning up expired invitations:", error);
      return 0;
    }
  }

  /**
   * Send invitation email to guest
   */
  private static async sendInvitationEmail(
    invitation: GuestInvitation,
    token: string,
  ): Promise<{ emailSent: boolean; invitationUrl: string; error?: string }> {
    const fallbackBaseUrl = import.meta.env.PROD
      ? "https://myhu.world"
      : window.location.origin;
    const fallbackInvitationUrl = `${fallbackBaseUrl}/accept-invitation?token=${token}`;

    try {
      // Always use production domain for invitation URLs in production
      // Check multiple ways to determine if we're in production
      const isProduction =
        import.meta.env.PROD ||
        import.meta.env.MODE === "production" ||
        window.location.hostname === "app.myhu.world" ||
        window.location.hostname === "myhu.world";

      // Use environment variable if available, otherwise determine based on environment
      const frontendUrl = import.meta.env.VITE_FRONTEND_URL;
      let baseUrl: string;

      if (frontendUrl) {
        baseUrl = frontendUrl;
      } else if (isProduction) {
        baseUrl = "https://app.myhu.world";
      } else {
        baseUrl = window.location.origin;
      }

      const invitationUrl = `${baseUrl}/accept-invitation?token=${token}`;

      // Log for debugging
      console.log("Environment check:", {
        "import.meta.env.PROD": import.meta.env.PROD,
        "import.meta.env.MODE": import.meta.env.MODE,
        "import.meta.env.VITE_FRONTEND_URL": import.meta.env.VITE_FRONTEND_URL,
        "window.location.hostname": window.location.hostname,
        "window.location.origin": window.location.origin,
        isProduction: isProduction,
        frontendUrl: frontendUrl,
        baseUrl: baseUrl,
        invitationUrl: invitationUrl,
      });

      // Get current user email from auth context
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const hostEmail = user?.email || "Unknown host";

      // Resolve human-readable project name from conservation_projects
      let projectName: string | undefined;
      let projectDescription: string | undefined;
      if (invitation.project_id) {
        const { data: project } = await supabase
          .from("conservation_projects")
          .select("name, description")
          .eq("id", invitation.project_id)
          .maybeSingle();
        projectName = project?.name ?? undefined;
        projectDescription = project?.description ?? undefined;
      }

      // Prepare email data
      const emailData = {
        to: invitation.guest_email,
        invitation_url: invitationUrl,
        host_email: hostEmail,
        project_id: invitation.project_id,
        ...(projectName ? { project_name: projectName } : {}),
        ...(projectDescription
          ? { project_description: projectDescription }
          : {}),
      };

      // Log for debugging
      console.log("Sending invitation email:", emailData);

      // Call backend API to send email
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000";
      const response = await fetch(`${apiUrl}/api/send-invitation-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: "Unknown error" }));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // IMPORTANT: the backend may return HTTP 200 with { success: false } on SMTP failures.
      // Treat that as a failure so the UI doesn’t incorrectly report “sent”.
      if (result?.success === false) {
        return {
          emailSent: false,
          invitationUrl,
          error: result?.message || "Email service failed to send.",
        };
      }

      if (result.dev_mode) {
        // Development mode - email service not configured
        console.info("📧 Development mode: Email service not configured");
        console.info(`🔗 Share this invitation link: ${invitationUrl}`);
        console.info(`📋 Token: ${token}`);
        return {
          emailSent: false,
          invitationUrl,
          error:
            "Email service is not configured in this environment (dev mode). Share the invitation link instead.",
        };
      } else {
        console.info("✅ Invitation email sent successfully");
        return { emailSent: true, invitationUrl };
      }
    } catch (error) {
      console.error("Error sending invitation email:", error);
      console.info(
        `⚠️ Email failed to send. Share this link manually: ${fallbackInvitationUrl}`,
      );
      return {
        emailSent: false,
        invitationUrl: fallbackInvitationUrl,
        error: (error as any)?.message || "Failed to send invitation email.",
      };
    }
  }

  /**
   * Get invitation by token
   */
  static async getInvitationByToken(token: string): Promise<{
    success: boolean;
    invitation?: GuestInvitation;
    error?: string;
  }> {
    try {
      // Use the public function that works for unauthenticated users
      const { data, error } = await supabase
        .rpc("get_invitation_by_token", { invitation_token: token })
        .single();

      if (error || !data) {
        return {
          success: false,
          error: "Invitation not found or expired",
        };
      }

      // Map the function result to our interface format
      const invitation: GuestInvitation = {
        id: data.id,
        host_user_id: "", // Not exposed for security
        guest_email: data.guest_email,
        guest_user_id: null,
        project_id: data.project_id,
        invitation_token: token,
        status: data.status as
          | "pending"
          | "accepted"
          | "declined"
          | "expired"
          | "cancelled"
          | "deleted",
        permissions: data.permissions,
        invited_at: data.invited_at,
        accepted_at: null,
        expires_at: data.expires_at,
      };

      return {
        success: true,
        invitation,
      };
    } catch (error) {
      console.error("Error getting invitation by token:", error);
      return {
        success: false,
        error: "Failed to retrieve invitation details",
      };
    }
  }

  /**
   * Accept an invitation
   */
  static async acceptInvitation(
    invitationId: string,
    guestUserId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Use the secure RPC function instead of direct table update to avoid RLS issues
      const { data, error } = await supabase.rpc("accept_guest_invitation", {
        invitation_id: invitationId,
        accepting_user_id: guestUserId,
      });

      if (error) {
        console.error("RPC Error accepting invitation:", error);
        throw error;
      }

      if (data && !data.success) {
        console.error("Function Error accepting invitation:", data.error);
        return {
          success: false,
          error: data.error || "Failed to accept invitation",
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error accepting invitation:", error);
      return {
        success: false,
        error: "Failed to accept invitation",
      };
    }
  }

  /**
   * Decline an invitation
   */
  static async declineInvitation(
    invitationId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("guest_invitations")
        .update({
          status: "declined",
        })
        .eq("id", invitationId)
        .eq("status", "pending")
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error declining invitation:", error);
      return {
        success: false,
        error: "Failed to decline invitation",
      };
    }
  }

  /**
   * Clear invitation history (mark non-pending invitations as deleted)
   */
  static async clearInvitationHistory(
    hostUserId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(
        `Attempting to clear invitation history for host ${hostUserId}`,
      );

      // First, check ALL invitations to see what statuses exist
      const { data: allInvitations, error: fetchError } = await supabase
        .from("guest_invitations")
        .select("id, status, guest_email, invited_at")
        .eq("host_user_id", hostUserId);

      if (fetchError) {
        console.error("Error fetching invitations:", fetchError);
        throw fetchError;
      }

      console.log(
        `Found ${allInvitations?.length || 0} total invitations:`,
        allInvitations,
      );

      if (!allInvitations || allInvitations.length === 0) {
        console.log("No invitations found at all");
        return { success: true };
      }

      // Filter to find non-pending invitations
      const completedInvitations = allInvitations.filter(
        (inv) => inv.status !== "pending",
      );
      console.log(
        `Found ${completedInvitations.length} completed invitations to clear:`,
        completedInvitations,
      );

      if (completedInvitations.length === 0) {
        console.log(
          "No completed invitations found to clear - all are pending",
        );
        return { success: true };
      }

      // Since DELETE policy is missing, we'll mark them as 'deleted' instead
      console.log(
        "Attempting to mark completed invitations as deleted (UPDATE approach)",
      );
      const idsToUpdate = completedInvitations.map((inv) => inv.id);
      const { error: updateError, count } = await supabase
        .from("guest_invitations")
        .update({ status: "deleted" })
        .in("id", idsToUpdate);

      if (updateError) {
        console.error("Update approach failed:", updateError);
        throw updateError;
      }

      console.log(
        `Successfully marked ${count} invitation records as deleted for host ${hostUserId}`,
      );
      return { success: true };
    } catch (error) {
      console.error("Error clearing invitation history:", error);
      return {
        success: false,
        error: error.message || "Failed to clear invitation history",
      };
    }
  }
}
